<script lang="ts" setup>
import { ref, toRefs, watch } from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { Filter, GroupFilterMode, SearchQuery, SortKey, SortOption } from '../../types';
import useSearch from '../../use-search';
import useAggregation from '../../use-aggregation';
import { SubscriptionTypeFilter } from '../../content-filters';
import { useDisplay } from 'vuetify';
import useLocale from '../../composables/useLocale';
import SelectedFilters from './SelectedFilters.vue';
import useSortOptions from '../use-sort-options';
import { Field } from '../../search-panel-types';
import { Capacitor } from '@capacitor/core';
import SearchInput from './SearchInput.vue';
import type { ContentType } from '@ilteducation/content-registry-types';
import { useFilters } from '../../composables/use-filters';
import { getSearchSource } from '../../search-source';

type Emits = {
  (e: 'toggleFilter'): void;
  (e: 'onSearch', properties: Record<string, any>): void;
  (e: 'back'): void;
};
const emit = defineEmits<Emits>();

type Props = {
  contentType: ContentType;
  focused?: boolean;
  /**
   * Writes the search text to the route (q). Searches started by a filter change read q from the route,
   * so with this off, the typed text is not kept when a filter changes.
   */
  enableSearchQuery?: boolean;
  filterFields: Field[];
  sortFields?: SortKey[];
  filters?: Filter;
  label?: string;
  limit?: number;
  selectedSortOption?: string;
  topMenuHeight?: string | number;
  searchSubscription?: string[] | string;
  estimatedNrGridCells: number;
  maxFilterCriteriaCount?: number;
  minAge?: number;
  maxAge?: number;
  rounded?: string;
  randomize?: boolean;
  /**
   * How repeated values for the same filter key combine. 'and' requires a hit to carry all of them,
   * 'or' any one of them. Defaults to 'and' so existing callers are unaffected.
   */
  groupFilterMode?: GroupFilterMode;
  backButtonLabel?: string;
};
const props = withDefaults(defineProps<Props>(), {
  focused: true,
  enableSearchQuery: true,
  filterFields: undefined,
  sortFields: undefined,
  filters: undefined,
  label: undefined,
  limit: undefined,
  selectedSortOption: undefined,
  maxFilterCriteriaCount: 1000,
  dynamicTopPadding: undefined,
  groupFilterMode: 'and',
  backButtonLabel: undefined,
});

const { contentType } = toRefs(props);
const searchStore = useSearch(contentType.value);
const aggregateStore = useAggregation(contentType.value);
const { search, clear } = searchStore;
const { t } = useLocale();
const { isLoading, total } = storeToRefs(searchStore);
const { aggregate } = aggregateStore;
const sortOptions = useSortOptions(props.sortFields);

const { mdAndUp, smAndDown } = useDisplay();
const isPopupOpen = ref(true);
const filterDefinitions = ref({});
const selectedSortOption = ref<SortOption>(
  sortOptions.value.find((s) => s.key === props.selectedSortOption) ?? sortOptions.value[0],
);
const { searchQuery, getFormattedFilters, getAggregationKeys, filterVisibleFields } = useFilters();

const groupFilteringMode = props.groupFilterMode;

const mergeFilter = (base: Filter, extra: Filter): Filter => {
  const out: Filter = { ...base };
  for (const [key, val] of Object.entries(extra)) {
    const prev = out[key];
    // A boolean can't be combined with values, so the extra value wins
    if (prev === undefined || typeof prev === 'boolean' || typeof val === 'boolean') {
      out[key] = val;
    } else {
      // Either side can be a string or an array (route gives a string for a key with one value),
      // so flatten to keep all values, e.g. 'a' + 'c' → ['a', 'c']
      out[key] = [prev, val].flat();
    }
  }
  return out;
};

const route = useRoute();

// q comes from `query` (apps can pass their own), the filters always come from the route. The source is the one
// passed in, e.g. 'search_box' for a typed search, else the one the route carries (see getSearchSource).
const handleSearch = (query: SearchQuery = searchQuery.value, source = getSearchSource(route.query)) => {
  let filters = getFormattedFilters(props.minAge, props.maxAge);
  if (props.searchSubscription) {
    filters = mergeFilter(filters, SubscriptionTypeFilter(props.searchSubscription));
  }
  if (props.filters) {
    filters = mergeFilter(filters, props.filters);
  }

  search(
    query?.q || '',
    filters,
    props.limit ? props.limit : props.estimatedNrGridCells,
    selectedSortOption.value && selectedSortOption.value.sort ? [selectedSortOption.value.sort] : [],
    groupFilteringMode,
    props.randomize,
    source,
  );
  const aggregationKeys = getAggregationKeys(filterVisibleFields(props.filterFields ?? []));
  aggregate(aggregationKeys, filters, false, props.maxFilterCriteriaCount, query.q, groupFilteringMode);
  emit('onSearch', {
    contentType: contentType.value,
    query: query.q ?? '',
    filter: filters,
    limit: props.limit ? props.limit : props.estimatedNrGridCells,
    sort: [selectedSortOption.value ? selectedSortOption.value.sort : ''],
  });
};

const handleSort = (sortOption: SortOption) => {
  selectedSortOption.value = sortOption;
};

const handleToggleFilter = () => {
  isPopupOpen.value = !isPopupOpen.value;
  emit('toggleFilter');
};

// Expose function to parent
defineExpose({ handleSearch, handleSort, selectedSortOption });

const handleClear = (source?: string) => {
  clear();
  handleSearch({ q: '' }, source);
};

// URL -> Search. searchQuery only changes while this panel's page is the current route (see useFilters),
// so a kept-alive panel doesn't search when another page, e.g. a detail page, changes the route.
watch(searchQuery, () => handleSearch());
// The sort is not in the route, so it can't carry a source there
watch(selectedSortOption, () => handleSearch(searchQuery.value, 'filter_or_sort'));
</script>

<template>
  <div
    class="search-header py-md-6 py-lg-6 py-xl-8 py-xxl-9 pb-4 bg-surface-container"
    :class="Capacitor.isNativePlatform() || smAndDown ? 'pt-4' : 'py-4'"
    :style="topMenuHeight ? { top: topMenuHeight } : {}"
  >
    <div class="bg-surface-container">
      <v-btn
        v-if="backButtonLabel && smAndDown"
        :ripple="{ class: 'text-primary' }"
        variant="text"
        color="on-surface-variant"
        class="back-btn ml-n3 mb-4"
        @click="emit('back')"
      >
        <v-icon size="20" class="mr-1">mdi-chevron-left</v-icon>
        <span>{{ backButtonLabel }}</span>
      </v-btn>
      <div class="d-flex">
        <SearchInput
          :filter-definitions="filterDefinitions"
          :placeholder="label ? label : t('content_search.label_search_bar')"
          :enable-search-query="enableSearchQuery"
          density="comfortable"
          :rounded="props.rounded || 'lg'"
          hide-details
          :focused="props.focused"
          @submit="handleSearch"
          @reset="handleClear"
        />
        <!-- Filter toggle buttons -->
        <!-- Visible only md+ -->
        <v-btn
          color="primary"
          variant="flat"
          class="filter-btn text-on-primary ml-4 d-none d-md-flex"
          :rounded="props.rounded || 'lg'"
          @click="handleToggleFilter"
          :aria-haspopup="true"
          :aria-expanded="isPopupOpen"
          data-testid="filter-btn"
        >
          {{ t('content_search.label_search_filters') }}
          <v-icon aria-hidden="true">mdi-filter-variant</v-icon>
        </v-btn>
        <!-- Visible only xs, sm -->
        <v-btn
          icon="mdi-filter-variant"
          color="primary"
          variant="flat"
          size="48"
          class="filter-btn text-on-primary d-flex d-md-none ml-4"
          :aria-label="t('content_search.label_search_filters')"
          @click="handleToggleFilter"
        />
        <!-- END Filter toggle buttons -->
      </div>

      <SelectedFilters
        :content-type="contentType"
        :class="{
          'pt-2': !mdAndUp,
          'pt-4': mdAndUp,
        }"
        :sort-options="sortOptions"
        :filter-fields="filterFields ?? []"
        :total-hits="total"
        :is-loading="isLoading"
        :selected-sort-option="props.selectedSortOption"
        :back-button-label="backButtonLabel"
        @change-sort="handleSort"
        @back="emit('back')"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.search-header {
  position: sticky;
  z-index: 999;
  transition: padding 0.2s ease-in-out;
}
.v-navigation-drawer {
  height: 100vh !important;
}

.back-btn {
  padding-inline: 12px;
  font-weight: 500;
  width: auto;
  text-transform: none;
  font-size: 16px;
}
</style>
